import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ExternalLink, Home, BookOpen, Star } from 'lucide-react';
import { booksData } from '@/data/books';

export default function BooksPage() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language as 'it' | 'en';

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const cardVariants = {
    hidden: { y: 40, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: 'spring', stiffness: 80, damping: 15 }
    }
  };

  return (
    <>
      <Helmet>
        <title>{currentLang === 'it' ? 'Libri di Cristian Tesconi | Serie Tecnologia e Informatica' : 'Books by Cristian Tesconi | Technology & Computer Science Series'}</title>
        <meta name="description" content={currentLang === 'it' ? 'Tutti i libri di Cristian Tesconi su Amazon - serie Tecnologia e Informatica. Python, C, Linux, Intelligenza Artificiale, Agenti AI e molto altro.' : 'All books by Cristian Tesconi on Amazon - Technology & Computer Science series. Python, C, Linux, Artificial Intelligence, AI Agents and more.'} />
        <meta name="keywords" content="cristian tesconi libri, cristian tesconi amazon, libri programmazione python, libri intelligenza artificiale, cristian tesconi autore" />
        <link rel="canonical" href="https://cristiantesconi.it/books" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cristiantesconi.it/books" />
        <meta property="og:title" content={currentLang === 'it' ? 'Libri di Cristian Tesconi | Tecnologia e Informatica' : 'Books by Cristian Tesconi | Technology & Computer Science'} />
        <meta property="og:description" content={currentLang === 'it' ? 'Tutti i libri di Cristian Tesconi - Python, C, Linux, AI, Agenti AI.' : 'All books by Cristian Tesconi - Python, C, Linux, AI, AI Agents.'} />
        <meta property="og:image" content="https://cristiantesconi.it/hero.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://cristiantesconi.it/hero.png" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": currentLang === 'it' ? "Libri di Cristian Tesconi" : "Books by Cristian Tesconi",
            "url": "https://cristiantesconi.it/books",
            "author": { "@type": "Person", "name": "Cristian Tesconi", "url": "https://cristiantesconi.it" },
            "description": currentLang === 'it' ? "Libri tecnici sulla programmazione e intelligenza artificiale scritti da Cristian Tesconi" : "Technical books on programming and artificial intelligence by Cristian Tesconi"
          })}
        </script>
      </Helmet>

      <div className="pt-20 sm:pt-24 pb-16 sm:pb-20 min-h-screen bg-surface dark:bg-dark-bg">
        {/* Hero header */}
        <div className="relative overflow-hidden bg-gradient-to-br from-primary/5 via-transparent to-secondary/5 dark:from-primary/10 dark:via-dark-bg dark:to-secondary/10 border-b-2 border-border dark:border-border-dark mb-12">
          <div className="max-w-[1600px] mx-auto px-4 py-12 sm:py-16">
            <div className="mb-6">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-text-secondary dark:text-text-light hover:text-primary transition-colors text-sm font-medium"
              >
                <Home className="h-4 w-4 flex-shrink-0" />
                <span>{t('common.back')} Home</span>
              </Link>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <div className="flex items-center justify-center gap-3 mb-4">
                <BookOpen className="h-8 w-8 text-primary" />
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-primary">
                  {t('books.title')}
                </h1>
              </div>
              <p className="text-sm sm:text-base text-text-secondary dark:text-text-light max-w-2xl mx-auto">
                {t('books.subtitle')}
              </p>
              <div className="mt-4 flex items-center justify-center gap-2">
                <span className="text-sm font-semibold text-primary">{booksData.length}</span>
                <span className="text-sm text-text-muted dark:text-text-light">
                  {currentLang === 'it' ? 'libri pubblicati nella serie "Tecnologia e Informatica"' : 'books published in the "Tecnologia e Informatica" series'}
                </span>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Books grid */}
        <div className="max-w-[1600px] mx-auto px-4">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 sm:gap-6 mb-12"
          >
            {booksData.map((book, index) => (
              <motion.div
                key={index}
                variants={cardVariants}
                whileHover={{ y: -8, scale: 1.02 }}
                transition={{ type: 'spring', stiffness: 300 }}
                className="group relative flex flex-col bg-surface dark:bg-dark-bg-elevated border-2 border-border dark:border-primary/20 rounded-2xl overflow-hidden shadow-lg dark:shadow-primary/10 hover:border-primary dark:hover:border-primary/50 hover:shadow-2xl hover:shadow-primary/20 dark:hover:shadow-primary/30 transition-all duration-300"
              >
                {/* Volume badge */}
                {book.volume && (
                  <div className="absolute top-3 left-3 z-10 flex items-center gap-1 px-2.5 py-1 bg-gradient-to-r from-primary to-secondary rounded-full shadow-lg">
                    <Star className="h-3 w-3 text-white" />
                    <span className="text-xs font-bold text-white">Vol. {book.volume}</span>
                  </div>
                )}

                {/* Cover image */}
                <div className="relative aspect-[3/4] bg-gradient-to-br from-primary/20 via-primary/10 to-secondary/10 overflow-hidden">
                  {book.coverImage ? (
                    <img
                      src={book.coverImage}
                      alt={book.title[currentLang]}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <BookOpen className="h-20 w-20 text-primary/30" />
                    </div>
                  )}
                  {/* Overlay on hover */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  {/* Amazon button on hover */}
                  <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                    <a
                      href={book.amazonLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full py-2.5 bg-gradient-to-r from-primary to-secondary text-white font-bold rounded-xl shadow-lg text-sm hover:opacity-90 transition-opacity"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {t('books.viewOnAmazon')}
                      <ExternalLink className="h-4 w-4 flex-shrink-0" />
                    </a>
                  </div>
                </div>

                {/* Card content */}
                <div className="flex flex-col flex-1 p-5">
                  <h3 className="text-base font-bold mb-1.5 text-text-primary dark:text-surface leading-snug group-hover:text-primary dark:group-hover:text-primary transition-colors">
                    {book.title[currentLang]}
                  </h3>

                  {book.subtitle && (
                    <p className="text-xs font-semibold text-primary/80 dark:text-secondary/80 mb-3 leading-snug">
                      {book.subtitle[currentLang]}
                    </p>
                  )}

                  <p className="text-sm text-text-secondary dark:text-text-light leading-relaxed flex-1">
                    {book.description[currentLang]}
                  </p>

                  {/* Footer link */}
                  <div className="mt-4 pt-4 border-t border-border/40 dark:border-border-dark/40 flex items-center justify-between">
                    <a
                      href={book.amazonLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary/10 to-secondary/10 hover:from-primary hover:to-secondary text-primary hover:text-white border border-primary/30 hover:border-transparent rounded-lg font-semibold text-sm transition-all duration-300"
                    >
                      {t('books.viewOnAmazon')}
                      <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" />
                    </a>
                    {book.series && (
                      <span className="text-xs text-text-muted dark:text-text-light/60">
                        {book.series}
                      </span>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Amazon author page CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="text-center py-8 border-t-2 border-border dark:border-border-dark"
          >
            <p className="text-text-secondary dark:text-text-light mb-4 text-sm sm:text-base">
              {currentLang === 'it'
                ? 'Trova tutti i miei libri sulla mia pagina autore su Amazon'
                : 'Find all my books on my Amazon author page'}
            </p>
            <a
              href="https://www.amazon.it/stores/author/B0CC29G6VV"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-primary to-secondary text-white font-bold rounded-xl hover:opacity-90 transition-all hover:scale-105 shadow-xl shadow-primary/40 text-sm sm:text-base"
            >
              <BookOpen className="h-5 w-5 flex-shrink-0" />
              {currentLang === 'it' ? 'Pagina Autore su Amazon' : 'Amazon Author Page'}
              <ExternalLink className="h-4 w-4 flex-shrink-0" />
            </a>
          </motion.div>
        </div>
      </div>
    </>
  );
}
